import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { FormField } from "@/pages/ContactPage";
import { PlusCircle, UserPlus } from "lucide-react";

export default function AdminClients() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", company: "", password: "" });
  const [saving, setSaving] = useState(false);

  const load = () =>
    api.get("/admin/clients").then((r) => {
      setClients(r.data);
      setLoading(false);
    });

  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/admin/clients", form);
      toast.success("Client account created.");
      setForm({ email: "", name: "", company: "", password: "" });
      setShowForm(false);
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-slate-500">Clients</div>
          <h1 className="font-display text-3xl md:text-4xl tracking-tighter font-medium mt-2">Client accounts</h1>
        </div>
        <button
          data-testid="new-client-btn"
          onClick={() => setShowForm((v) => !v)}
          className="inline-flex items-center gap-2 px-4 py-2 bg-[#1E40AF] hover:bg-[#1E3A8A] text-white text-sm font-semibold rounded-sm"
        >
          <UserPlus className="h-4 w-4" /> Invite client
        </button>
      </div>

      {showForm && (
        <form data-testid="new-client-form" onSubmit={create} className="border border-slate-200 bg-white p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField label="Contact name" required id="c-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} testId="client-name" />
          <FormField label="Company" required id="c-co" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} testId="client-company" />
          <FormField label="Email" type="email" required id="c-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} testId="client-email" />
          <FormField label="Initial password (min 8 chars)" type="text" required id="c-pass" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} testId="client-password" />
          <div className="md:col-span-2 flex items-center justify-end gap-3">
            <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 text-sm text-slate-600 hover:text-slate-900">Cancel</button>
            <button data-testid="create-client-btn" disabled={saving} className="px-5 py-2 bg-[#1E40AF] hover:bg-[#1E3A8A] text-white text-sm font-semibold rounded-sm disabled:opacity-60">
              {saving ? "Creating…" : "Create client account"}
            </button>
          </div>
        </form>
      )}

      <div className="bg-white border border-slate-200">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-[0.2em] text-slate-500">
            <tr>
              <th className="text-left px-5 py-3 font-semibold">Company</th>
              <th className="text-left px-5 py-3 font-semibold">Contact</th>
              <th className="text-left px-5 py-3 font-semibold">Email</th>
              <th className="text-right px-5 py-3 font-semibold">Onboarded</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan="4" className="px-5 py-10 text-center text-slate-400">Loading…</td></tr>}
            {!loading && clients.length === 0 && (
              <tr><td colSpan="4" className="px-5 py-10 text-center text-slate-400">No client accounts yet.</td></tr>
            )}
            {clients.map((c) => (
              <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="px-5 py-3 font-medium">{c.company || "—"}</td>
                <td className="px-5 py-3 text-slate-700">{c.name}</td>
                <td className="px-5 py-3 text-slate-600">{c.email}</td>
                <td className="px-5 py-3 text-right text-xs text-slate-500 tabular-nums">
                  {c.created_at ? new Date(c.created_at).toLocaleDateString() : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
